import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { useBuilding, useMeetingRooms, useUserBookings } from "@/api";
import PageCardsPlaceholder from "@/components/PageCardsPlaceholder";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Bookings.module.scss";
import { Button, LoadingSpinner, SelectRow } from "@app-components";
import { useEffect, useState } from "react";
import AllRooms from "@/components/bookings/AllRooms";
import SearchForRooms from "@/components/bookings/SearchForRooms";
import RoomBookingCard from "@/components/bookings/RoomBookingCard";
import { WELCOME_BOOKING_URL } from "@/lib";
import { useWelcomeBookingAccess } from "@/hooks/useWelcomeBookingAccess";
import { useSessionState } from "@/hooks/useSessionState";
import { useLoggedInUser } from "@/store/zustandStore";

export const Route = createFileRoute("/_portal/bookings/")({
  component: Bookings,
});

function Bookings() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const user = useLoggedInUser();
  const navigate = useNavigate();
  const { allowWelcomeBookingAccess } = useWelcomeBookingAccess();
  const { data: building } = useBuilding();
  const { data: rooms, isLoading: isRoomsLoading } = useMeetingRooms(
    allowWelcomeBookingAccess,
  );
  const { data: bookings, isLoading } = useUserBookings(
    {
      userId: user?.userId,
      companyId: user?.buildingTenantId,
    },
    allowWelcomeBookingAccess,
  );
  const upcomingBookings = bookings?.upcoming;
  const [tab, setTab] = useSessionState<string>("meetingRoomsTab", "all");
  const [showAllBookings, setShowAllBookings] = useState(false);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      setTab(hash);
    }
  }, [location.hash]);

  const tabs = [
    {
      value: "all",
      label: t("PAGES.BOOKING.SUB_ROUTES.ALL_ROOMS"),
    },
    {
      value: "search",
      label: t("PAGES.BOOKING.SUB_ROUTES.SEARCH"),
    },
  ];

  if (!allowWelcomeBookingAccess) return null;

  return (
    <div className={`pageWithoutBottomSpacing ${styles.container}`}>
      <PageBreadcrumbs
        levels={[{ label: t("PAGES.BOOKING.HEADER"), path: "/bookings" }]}
      />
      <div
        className="pageHeader"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "end",
          justifyContent: "end",
          gap: "var(--wlcm-spacing-sm) var(--wlcm-spacing-md)",
        }}
      >
        <div style={{ flex: 1, minWidth: 300 }}>
          <h1>{t("PAGES.BOOKING.HEADER")}</h1>
          <p>
            <Trans
              i18nKey="PAGES.BOOKING.SUBHEADER"
              values={{ buildingName: building.displayName }}
            >
              Text
              <a href={WELCOME_BOOKING_URL(i18n.language)} target="_blank">
                link
              </a>
              .
            </Trans>
          </p>
        </div>
        <Button
          purple
          icon="arrow_forward"
          iconPosition="right"
          onClick={() => navigate({ to: "/bookings/history" })}
        >
          {t("ACCESS_CARD.DETAILS_MODAL.HISTORY")}
        </Button>
      </div>
      {isLoading || isRoomsLoading ? (
        <div
          style={{
            height: 250,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingSpinner />
        </div>
      ) : upcomingBookings?.length && rooms ? (
        <div className={styles.bookingsCardsGrid}>
          {(showAllBookings
            ? upcomingBookings
            : upcomingBookings.slice(0, 3)
          ).map((booking) => (
            <RoomBookingCard key={booking.id} booking={booking} />
          ))}
          {upcomingBookings.length > 3 && (
            <Button
              dark
              secondary
              icon={showAllBookings ? "expand_less" : "expand_more"}
              className={styles.showAllBookingsBtn}
              onClick={() => setShowAllBookings((prev) => !prev)}
            >
              {t(
                `BOOKINGS.TOGGLE_SHOW_ALL.${showAllBookings ? "HIDE" : "SHOW"}`,
                { count: upcomingBookings.length - 3 },
              )}
            </Button>
          )}
        </div>
      ) : (
        <PageCardsPlaceholder
          icon={["date_range", "meeting_room", "domain"]}
          placeholder={t("PAGES.BOOKING.PLACEHOLDER")}
        />
      )}
      <div className={styles.bookingContainer} id="bookings-container">
        <h2>{t("PAGES.BOOKING.BOOK")}</h2>
        <SelectRow
          tabs
          label="Tabs"
          hideLabel
          name="bookings-tabs"
          options={tabs}
          value={tab}
          onChange={(tab) => {
            navigate({
              hash: tab as string,
            });
          }}
        />
        <div className={styles.bookingContent}>
          {tab === "all" ? <AllRooms /> : <SearchForRooms />}
        </div>
      </div>
    </div>
  );
}
